<template>
  <a
    class="dropdown-item"
    :class="{ 'is-disabled': !canReorder }"
    :disabled="!canReorder || null"
    v-on:click.prevent="modal = canReorder"
  >
    <i class="icon-sort-asc"></i>
    Reorder elements by name
  </a>
  <Modal
    v-if="element"
    v-model="modal"
    title="Confirm reordering"
  >
    <p>
      This will reorder all elements directly within <strong>{{ element.name }}</strong> by their
      name, in ascending order.
    </p>
    <p>The previous ordering cannot be easily restored.</p>
    <template v-slot:footer="{ close }">
      <button
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        autofocus
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="loading"
        v-on:click="reorder"
      >
        Reorder
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useCorporaStore, useElementStore } from '@/stores'
import type { Element, ElementBase } from '@/types'

import Modal from '../Modal.vue'

const { element } = defineProps<{ element: ElementBase | Element }>()

const elementStore = useElementStore()
const corporaStore = useCorporaStore()

const canReorder = computed((): boolean => {
  return (
    elementStore.canWrite(element.id) ||
    (element.corpus !== null && corporaStore.corpora[element.corpus.id]?.rights.includes('write'))
  )
})
const modal = ref(false)
const loading = ref(false)

async function reorder() {
  if (loading.value || !canReorder.value) return
  loading.value = true
  try {
    await elementStore.reorderChildren(element.id)
    modal.value = false
  } catch {
    // Do nothing on errors, a notification is shown by the store and we just don't want to be closing the modal
  } finally {
    loading.value = false
  }
}
</script>
